import { forwardRef, useState, useEffect, useRef } from 'react';
import type { PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';
import { getLuminance } from 'polished';

import * as polarisSolidIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/polaris-solid.icon';
import type { NoChildrenProp, RefElement, WithAttributes } from '@pega/cosmos-react-core';
import { getVariantColors } from '@pega/cosmos-react-core/lib/components/Banner/Banner.styles';
import {
  Flex,
  Button,
  Text,
  Icon,
  useTheme,
  defaultThemeProp,
  Progress,
  ExpandCollapse,
  useI18n,
  registerIcon,
  markdownToPlainText,
  calculateFontSize,
  useLiveLog,
  useAfterInitialEffect,
  ExpandCollapseButton
} from '@pega/cosmos-react-core';
import { RichTextViewer } from '@pega/cosmos-react-rte';

registerIcon(polarisSolidIcon);

export type AISummaryProps = WithAttributes<
  'div',
  NoChildrenProp & {
    onGenerate: () => void;
    summary?: string;
    error?: boolean;
    loading?: boolean;
  }
>;

const StyledAISummary = styled(Flex)(({ theme }) => {
  const colors = getVariantColors('ai', theme);

  return css`
    background: ${colors.background};
    border-radius: ${theme.base['border-radius']};
    padding: ${theme.base.spacing};
    border: solid 0.0625rem ${colors.borderColor};
  `;
});

StyledAISummary.defaultProps = defaultThemeProp;

const StyledHeading = styled(Text)(({ theme }) => {
  const isLight = getLuminance(theme.base.palette['foreground-color']) > 0.5;
  const colors = getVariantColors('ai', theme);

  return css`
    color: ${isLight ? colors.textColor : colors.iconColor};
  `;
});

StyledHeading.defaultProps = defaultThemeProp;

const StyledFadedSummary = styled(Text)(({ theme }) => {
  const colors = getVariantColors('ai', theme);

  return css`
    white-space: nowrap;
    overflow: hidden;
    min-width: 0;
    mask-image: linear-gradient(
      to inline-end,
      ${colors.textColor} 0%,
      ${colors.textColor} 60%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      ${colors.textColor} 0%,
      ${colors.textColor} 60%,
      transparent 100%
    );
  `;
});

StyledFadedSummary.defaultProps = defaultThemeProp;

const StyledCollapsedSummaryContainer = styled(Flex)`
  min-width: 0;
`;
StyledCollapsedSummaryContainer.defaultProps = defaultThemeProp;

const StyledAISummaryDisclaimer = styled(Text)(({ theme }) => {
  const systemFontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']).xxs;

  return css`
    margin-inline-start: ${theme.base.spacing};
    font-size: ${systemFontSize};
  `;
});

StyledAISummaryDisclaimer.defaultProps = defaultThemeProp;

const AISummary = forwardRef<RefElement<AISummaryProps>, PropsWithoutRef<AISummaryProps>>(
  function AISummary({ onGenerate, summary, error, loading, ...restProps }, ref) {
    const t = useI18n();
    const theme = useTheme();
    const hadFocusRef = useRef(false);
    const [collapsed, setCollapsed] = useState(false);
    const [isFocusMoved, setIsFocusMoved] = useState(false);
    const [isTrackingFocus, setIsTrackingFocus] = useState(false);
    const generateButtonRef = useRef<HTMLButtonElement>(null);
    const showGenerateBtn = !loading && !(summary && collapsed);
    const showProgress = loading;
    const { announcePolite } = useLiveLog();

    useEffect(() => {
      if (!isTrackingFocus) return;

      const handlePointerDown = () => setIsFocusMoved(true);
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') setIsFocusMoved(true);
      };
      document.addEventListener('pointerdown', handlePointerDown);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('pointerdown', handlePointerDown);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isTrackingFocus]);

    useEffect(() => {
      if (!loading && hadFocusRef.current) {
        if (!isFocusMoved) {
          generateButtonRef.current?.focus();
        }
        hadFocusRef.current = false;
        setIsFocusMoved(false);
        setIsTrackingFocus(false);
      }
    }, [loading, isFocusMoved]);

    useAfterInitialEffect(() => {
      if (loading) {
        announcePolite({ message: t('generating_summary') });
      } else if (error) {
        announcePolite({ message: t('summary_generation_failed') });
      } else if (summary) {
        announcePolite({ message: t('summary_generated') });
      }
    }, [loading, summary, error]);

    const handleGenerate = () => {
      hadFocusRef.current = true;
      setIsFocusMoved(false);
      setIsTrackingFocus(true);
      onGenerate();
    };

    const toggleCollapse = () => {
      setCollapsed(currentState => !currentState);
    };

    const getGenerateBtnLabel = () => {
      if (error) return t('retry');
      if (summary) return t('regenerate');
      return t('generate');
    };

    const generateAction = (
      <Flex container={{ alignItems: 'center', gap: 0.5 }}>
        {error && <Text>{t('generation_failed')}</Text>}
        <Button ref={generateButtonRef} variant='link' onClick={handleGenerate}>
          {getGenerateBtnLabel()}
        </Button>
      </Flex>
    );

    return (
      <StyledAISummary {...restProps} container={{ direction: 'column' }} ref={ref}>
        <Flex container={{ justify: 'between', alignItems: 'center', gap: 2 }}>
          <Flex container={{ gap: 1, alignItems: 'center' }} item={{ shrink: 0 }}>
            {summary && (
              <ExpandCollapseButton
                expanded={!collapsed}
                onToggle={toggleCollapse}
                iconOnly
                compact
              />
            )}
            <Icon name='polaris-solid' color={theme.base.palette.ai} />
            <StyledHeading variant='h3'>{t('summary')}</StyledHeading>
          </Flex>
          {showGenerateBtn && (
            <Flex container={{ alignItems: 'center', justify: 'end' }} item={{ grow: 1 }}>
              {generateAction}
            </Flex>
          )}
          {summary && collapsed && (
            <StyledCollapsedSummaryContainer item={{ grow: 1 }} container={{ justify: 'end' }}>
              {error ? (
                generateAction
              ) : (
                <StyledFadedSummary>{markdownToPlainText(summary).slice(0, 60)}</StyledFadedSummary>
              )}
            </StyledCollapsedSummaryContainer>
          )}

          {showProgress && <Progress variant='ring' placement='inline' focusOnVisible />}
        </Flex>
        {summary && (
          <ExpandCollapse dimension='height' collapsed={collapsed}>
            <Flex container={{ direction: 'column', pad: [1, undefined, undefined] }}>
              <RichTextViewer content={summary} type='markdown' />
              <StyledAISummaryDisclaimer variant='secondary'>
                {t('ai_disclaimer')}
              </StyledAISummaryDisclaimer>
            </Flex>
          </ExpandCollapse>
        )}
      </StyledAISummary>
    );
  }
);

export default AISummary;
